// LeaveType.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const LeaveType = () => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [formData, setFormData] = useState({
    leave_name: '',
    leave_code: '',
    annual_limit: '',
    is_paid: 1,
    carry_forward: 0
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    loaded: false
  });

  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

  // Fetch user permissions
  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.permissions) {
        const leaveTypePermissions = response.data.permissions.find(
          p => p.module_name === 'LeaveTypes' || p.module_name === 'LeaveManagement'
        );

        if (leaveTypePermissions) {
          setUserPermissions({
            can_view: Boolean(leaveTypePermissions.can_view),
            can_add: Boolean(leaveTypePermissions.can_add),
            can_edit: Boolean(leaveTypePermissions.can_edit),
            can_delete: Boolean(leaveTypePermissions.can_delete),
            loaded: true
          });
        } else {
          setUserPermissions({
            can_view: true,
            can_add: true,
            can_edit: true,
            can_delete: true,
            loaded: true
          });
        }
      } else {
        setUserPermissions({
          can_view: true,
          can_add: true,
          can_edit: true,
          can_delete: true,
          loaded: true
        });
      }
    } catch (error) {
      setUserPermissions({
        can_view: true,
        can_add: true,
        can_edit: true,
        can_delete: true,
        loaded: true
      });
    }
  }, [API_BASE]);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE}leave/get_leave_types.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (response.data && response.data.success) {
        setLeaveTypes(response.data.leave_types || []);
        setError('');
      } else {
        setLeaveTypes([]);
        const errorMsg = response.data?.error || 'Failed to fetch leave types';
        setError(errorMsg);
      }
    } catch (error) {
      setLeaveTypes([]);
      const errorMsg = error.response?.data?.error ||
        error.message ||
        'Failed to fetch leave types';
      setError(errorMsg);
      console.error('Error fetching leave types:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        await fetchUserPermissions();
        await fetchLeaveTypes();
      } catch (error) {
        console.error('Initialization error:', error);
        setError(`Initialization failed: ${error.message}`);
      }
    };

    initializeData();
  }, [fetchLeaveTypes, fetchUserPermissions]);

  // Permission check functions
  const canViewLeaveTypes = () => userPermissions.can_view;
  const canAddLeaveType = () => userPermissions.can_add;
  const canEditLeaveType = () => userPermissions.can_edit;
  const canDeleteLeaveType = () => userPermissions.can_delete;

  const handleAdd = () => {
    if (!canAddLeaveType()) {
      alert('You do not have permission to add leave types');
      return;
    }
    setFormData({
      leave_name: '',
      leave_code: '',
      annual_limit: '',
      is_paid: 1,
      carry_forward: 0
    });
    setValidationErrors({});
    setShowAddModal(true);
  };

  const handleEdit = (leaveType) => {
    if (!canEditLeaveType()) {
      alert('You do not have permission to edit leave types');
      return;
    }
    setSelectedLeaveType(leaveType);
    setFormData({
      leave_name: leaveType.leave_name,
      leave_code: leaveType.leave_code,
      annual_limit: leaveType.annual_limit,
      is_paid: leaveType.is_paid,
      carry_forward: leaveType.carry_forward
    });
    setValidationErrors({});
    setShowEditModal(true);
  };

  const handleDelete = (leaveType) => {
    if (!canDeleteLeaveType()) {
      alert('You do not have permission to delete leave types');
      return;
    }
    setSelectedLeaveType(leaveType);
    setShowDeleteModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.leave_name.trim()) {
      errors.leave_name = 'Leave type name is required';
    } else if (formData.leave_name.length > 100) {
      errors.leave_name = 'Leave type name cannot exceed 100 characters';
    }

    if (!formData.leave_code.trim()) {
      errors.leave_code = 'Leave type code is required';
    } else if (formData.leave_code.length > 20) {
      errors.leave_code = 'Leave type code cannot exceed 20 characters';
    }

    if (formData.annual_limit && parseFloat(formData.annual_limit) < 0) {
      errors.annual_limit = 'Annual limit cannot be negative';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}leave/add_leave_type.php`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        await fetchLeaveTypes();
        setShowAddModal(false);
        alert('Leave type added successfully');
      } else {
        setError(response.data.error || 'Failed to add leave type');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add leave type');
      console.error('Error adding leave type:', error);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}leave/update_leave_type.php`,
        {
          leave_type_id: selectedLeaveType.leave_type_id,
          ...formData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        await fetchLeaveTypes();
        setShowEditModal(false);
        alert('Leave type updated successfully');
      } else {
        setError(response.data.error || 'Failed to update leave type');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update leave type');
      console.error('Error updating leave type:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}leave/delete_leave_type.php`,
        { leave_type_id: selectedLeaveType.leave_type_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        await fetchLeaveTypes();
        setShowDeleteModal(false);
        alert('Leave type deleted successfully');
      } else {
        setError(response.data.error || 'Failed to delete leave type');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete leave type');
      console.error('Error deleting leave type:', error);
    }
  };

  const filteredLeaveTypes = leaveTypes.filter(leaveType => {
    return (
      leaveType.leave_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leaveType.leave_code?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const sortedLeaveTypes = [...filteredLeaveTypes].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.leave_name.localeCompare(b.leave_name);
      case 'code':
        return a.leave_code.localeCompare(b.leave_code);
      case 'limit':
        return (b.annual_limit || 0) - (a.annual_limit || 0);
      default:
        return a.leave_type_id - b.leave_type_id;
    }
  });

  if ((loading && leaveTypes.length === 0) || !userPermissions.loaded) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading leave types...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canViewLeaveTypes()) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-5">
                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                <h3 className="mt-3 text-warning">Access Denied</h3>
                <p className="text-muted">You do not have permission to view leave types.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => navigate('/admin-panel')}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate('/admin-panel')}
                        title="Go to Dashboard"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate('/Menu')}
                        title="Go to Menu"
                      >
                        <i className="bi bi-menu-button"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-calendar2-week me-2 text-info"></i>
                        Leave Types Management
                        {!canAddLeaveType() && !canEditLeaveType() && !canDeleteLeaveType() && (
                          <span className="badge bg-warning ms-2">View Only</span>
                        )}
                      </h2>
                      <p className="text-muted mb-0">
                        Manage different types of leaves for the system
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-outline-secondary me-2"
                      onClick={() => navigate('/admin-panel')}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Back to Dashboard
                    </button>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => navigate('/Menu')}
                    >
                      <i className="bi bi-file-earmark-text me-2"></i>
                      Menu
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/admin-panel')}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Dashboard
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/employees')}
                  >
                    <i className="bi bi-people me-2"></i>
                    Employees
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-info w-100 d-flex align-items-center justify-content-center"
                    disabled
                  >
                    <i className="bi bi-calendar2-week me-2"></i>
                    Leave Types
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/leave-master')}
                  >
                    <i className="bi bi-calendar-check me-2"></i>
                    Leave Master
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/pay-components')}
                  >
                    <i className="bi bi-calculator me-2"></i>
                    Pay Components
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/Menu')}
                  >
                    <i className="bi bi-menu-button me-2"></i>
                    Menu
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>
                <strong>Error:</strong> {error}
                <div className="mt-2">
                  <button
                    className="btn btn-sm btn-outline-danger me-2"
                    onClick={fetchLeaveTypes}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Retry
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setError('')}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Action Bar */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search leave types..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary border-start-0"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="code">Sort by Code</option>
                    <option value="limit">Sort by Annual Limit</option>
                  </select>
                </div>
                <div className="col-md-5 text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-outline-secondary me-2"
                      onClick={fetchLeaveTypes}
                      title="Refresh"
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                    </button>
                    {canAddLeaveType() && (
                      <button
                        className="btn btn-info text-white"
                        onClick={handleAdd}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Leave Type
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Types Table */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {sortedLeaveTypes.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-calendar2-week display-1 text-muted"></i>
                  <h4 className="mt-3 text-muted">No leave types found</h4>
                  <p className="text-muted">
                    {leaveTypes.length === 0
                      ? "No leave types available. Add your first leave type."
                      : "No leave types match your search criteria."
                    }
                  </p>
                  {leaveTypes.length === 0 && canAddLeaveType() && (
                    <button
                      className="btn btn-info text-white mt-2"
                      onClick={handleAdd}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add First Leave Type
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Leave Type</th>
                        <th>Code</th>
                        <th>Annual Limit (Days)</th>
                        <th>Paid Leave</th>
                        <th>Carry Forward</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedLeaveTypes.map((leaveType) => (
                        <tr key={leaveType.leave_type_id} className="leave-type-row">
                          <td className="ps-4">
                            <div className="d-flex align-items-center">
                              <div className="leave-type-icon bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{ width: '40px', height: '40px' }}
                              >
                                <i className="bi bi-calendar2-week"></i>
                              </div>
                              <div>
                                <div className="fw-semibold">
                                  {leaveType.leave_name}
                                </div>
                                <small className="text-muted">
                                  ID: {leaveType.leave_type_id}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-primary">{leaveType.leave_code}</span>
                          </td>
                          <td>
                            {leaveType.annual_limit ? (
                              <span className="fw-semibold">{leaveType.annual_limit}</span>
                            ) : (
                              <span className="text-muted">No limit</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${leaveType.is_paid === 1 ? 'bg-success' : 'bg-secondary'
                              }`}>
                              {leaveType.is_paid === 1 ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${leaveType.carry_forward === 1 ? 'bg-info' : 'bg-secondary'
                              }`}>
                              {leaveType.carry_forward === 1 ? 'Allowed' : 'Not Allowed'}
                            </span>
                          </td>
                          <td className="text-end pe-4">
                            <div className="btn-group" role="group">
                              {canEditLeaveType() && (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleEdit(leaveType)}
                                  title="Edit Leave Type"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              )}
                              {canDeleteLeaveType() && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(leaveType)}
                                  title="Delete Leave Type"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3">
                  <div className="fw-bold text-primary fs-4">{leaveTypes.length}</div>
                  <small className="text-muted">Total Leave Types</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-success fs-4">
                    {leaveTypes.filter(lt => lt.is_paid === 1).length}
                  </div>
                  <small className="text-muted">Paid Leave Types</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-info fs-4">
                    {leaveTypes.filter(lt => lt.carry_forward === 1).length}
                  </div>
                  <small className="text-muted">Carry Forward Allowed</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-warning fs-4">
                    {leaveTypes.filter(lt => lt.annual_limit > 0).length}
                  </div>
                  <small className="text-muted">With Annual Limits</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0">
            <div className="card-body text-center py-3">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-primary"
                  onClick={fetchLeaveTypes}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Refresh Data
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={() => navigate('/employees')}
                >
                  <i className="bi bi-people me-2"></i>
                  Manage Employees
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={() => navigate('/leave-master')}
                >
                  <i className="bi bi-calendar-check me-2"></i>
                  Leave Master
                </button>
                {canAddLeaveType() && (
                  <button
                    className="btn btn-info text-white"
                    onClick={handleAdd}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Add New Leave Type
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Leave Type Modal */}
      {showAddModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{
                background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                borderBottom: 'none'
              }}>
                <h5 className="modal-title text-white">
                  <i className="bi bi-plus-circle me-2"></i>
                  Add New Leave Type
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>

              <form onSubmit={handleAddSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="leave_name" className="form-label fw-medium">
                      Leave Type Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.leave_name ? 'is-invalid' : ''}`}
                      id="leave_name"
                      name="leave_name"
                      value={formData.leave_name}
                      onChange={handleChange}
                      placeholder="e.g., Casual Leave, Sick Leave, Earned Leave"
                      required
                    />
                    {validationErrors.leave_name && (
                      <div className="invalid-feedback">{validationErrors.leave_name}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="leave_code" className="form-label fw-medium">
                      Leave Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.leave_code ? 'is-invalid' : ''}`}
                      id="leave_code"
                      name="leave_code"
                      value={formData.leave_code}
                      onChange={handleChange}
                      placeholder="e.g., CL, SL, EL"
                      required
                    />
                    {validationErrors.leave_code && (
                      <div className="invalid-feedback">{validationErrors.leave_code}</div>
                    )}
                    <div className="form-text">Short code for the leave type (max 20 characters)</div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="annual_limit" className="form-label fw-medium">
                      Annual Limit (Days)
                    </label>
                    <input
                      type="number"
                      className={`form-control ${validationErrors.annual_limit ? 'is-invalid' : ''}`}
                      id="annual_limit"
                      name="annual_limit"
                      value={formData.annual_limit}
                      onChange={handleChange}
                      placeholder="e.g., 12"
                      step="0.5"
                      min="0"
                    />
                    {validationErrors.annual_limit && (
                      <div className="invalid-feedback">{validationErrors.annual_limit}</div>
                    )}
                    <div className="form-text">Leave 0 or empty for no limit</div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="is_paid"
                          name="is_paid"
                          checked={formData.is_paid === 1}
                          onChange={handleChange}
                        />
                        <label className="form-check-label fw-medium" htmlFor="is_paid">
                          Paid Leave
                        </label>
                        <div className="form-text">Employee gets paid during this leave</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="carry_forward"
                          name="carry_forward"
                          checked={formData.carry_forward === 1}
                          onChange={handleChange}
                        />
                        <label className="form-check-label fw-medium" htmlFor="carry_forward">
                          Allow Carry Forward
                        </label>
                        <div className="form-text">Unused leaves can be carried to next year</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light border-secondary-subtle"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-info text-white"
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Add Leave Type
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Leave Type Modal */}
      {showEditModal && selectedLeaveType && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{
                background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                borderBottom: 'none'
              }}>
                <h5 className="modal-title text-white">
                  <i className="bi bi-pencil-square me-2"></i>
                  Edit Leave Type
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>

              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="leave_name" className="form-label fw-medium">
                      Leave Type Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.leave_name ? 'is-invalid' : ''}`}
                      id="leave_name"
                      name="leave_name"
                      value={formData.leave_name}
                      onChange={handleChange}
                      required
                    />
                    {validationErrors.leave_name && (
                      <div className="invalid-feedback">{validationErrors.leave_name}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="leave_code" className="form-label fw-medium">
                      Leave Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.leave_code ? 'is-invalid' : ''}`}
                      id="leave_code"
                      name="leave_code"
                      value={formData.leave_code}
                      onChange={handleChange}
                      required
                    />
                    {validationErrors.leave_code && (
                      <div className="invalid-feedback">{validationErrors.leave_code}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="annual_limit" className="form-label fw-medium">
                      Annual Limit (Days)
                    </label>
                    <input
                      type="number"
                      className={`form-control ${validationErrors.annual_limit ? 'is-invalid' : ''}`}
                      id="annual_limit"
                      name="annual_limit"
                      value={formData.annual_limit}
                      onChange={handleChange}
                      step="0.5"
                      min="0"
                    />
                    {validationErrors.annual_limit && (
                      <div className="invalid-feedback">{validationErrors.annual_limit}</div>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="is_paid"
                          name="is_paid"
                          checked={formData.is_paid === 1}
                          onChange={handleChange}
                        />
                        <label className="form-check-label fw-medium" htmlFor="is_paid">
                          Paid Leave
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="carry_forward"
                          name="carry_forward"
                          checked={formData.carry_forward === 1}
                          onChange={handleChange}
                        />
                        <label className="form-check-label fw-medium" htmlFor="carry_forward">
                          Allow Carry Forward
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light border-secondary-subtle"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-info text-white"
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Update Leave Type
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedLeaveType && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Confirm Delete
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>

              <div className="modal-body">
                <div className="alert alert-warning" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <strong>Warning:</strong> This action cannot be undone.
                </div>

                <p>Are you sure you want to delete the following leave type?</p>

                <div className="card border-0 bg-light">
                  <div className="card-body">
                    <h6 className="fw-bold">{selectedLeaveType.leave_name}</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <small className="text-muted d-block">Code</small>
                        <strong>{selectedLeaveType.leave_code}</strong>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted d-block">Annual Limit</small>
                        <strong>{selectedLeaveType.annual_limit || 'No limit'} days</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-danger mt-3" role="alert">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Note:</strong> Deleting this leave type may affect existing leave allocations.
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light border-secondary-subtle"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                >
                  <i className="bi bi-trash me-2"></i>
                  Delete Leave Type
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveType;